import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, CheckCircle, MoreVertical, Trash2, Edit } from 'lucide-react';
import { Project } from '../../types';
import { Badge, getPriorityVariant, getStatusVariant, statusLabels, priorityLabels } from '../common/Badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ProjectCardProps {
  project: Project;
  onDelete: (id: string) => void;
  onEdit: (project: Project) => void;
}


export function ProjectCard({ project, onDelete, onEdit }: ProjectCardProps) {
  const [showMenu, setShowMenu] = React.useState(false);
  const navigate = useNavigate();
  const progress = project.task_count
    ? Math.round(((project.completed_task_count || 0) / Number(project.task_count)) * 100)
    : 0;
  const projectUrl = `/projects/${project.id}`;

  const handleOpenProject = () => {
    navigate(projectUrl);
  };

  return (
    <div
      className="group bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      role="link"
      tabIndex={0}
      onClick={handleOpenProject}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleOpenProject();
        }
      }}
    >
      {/* Color bar */}
      <div className="h-1.5" style={{ backgroundColor: project.color }} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold text-gray-900 transition-colors line-clamp-1 group-hover:text-primary-600">
              {project.name}
            </div>
            {project.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{project.description}</p>
            )}
          </div>
          <div className="relative ml-2">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-7 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-36">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(project);
                    setShowMenu(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Edit size={14} />
                  Editar
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(project.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Trash2 size={14} />
                  Excluir
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex gap-2 mb-4">
          <Badge variant={getStatusVariant(project.status)}>
            {statusLabels[project.status]}
          </Badge>
          <Badge variant={getPriorityVariant(project.priority)}>
            {priorityLabels[project.priority] || project.priority}
          </Badge>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{project.completed_task_count || 0}/{project.task_count || 0} tarefas</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, backgroundColor: project.color }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Users size={13} />
            <span>{project.member_count || 0} membros</span>
          </div>
          {project.end_date && (
            <div className="flex items-center gap-1">
              <Calendar size={13} />
              <span>{format(new Date(project.end_date), "d 'de' MMM 'de' yyyy", { locale: ptBR })}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <CheckCircle size={13} />
            <span>{project.task_count || 0} tarefas</span>
          </div>
        </div>
      </div>
    </div>
  );
}
